/**
 * The selectors are based on [WAI-ARIA state properties](https://www.w3.org/WAI/PF/aria-1.1/states_and_properties) and common CSS Selectors
 * Based on the excellent work done in https://github.com/chakra-ui/chakra-ui
 */
const PseudoPropStyles = {
  _hover: '&:hover:not(:disabled)',
  /**
   * Styles for CSS Selector `&:active:not(:disabled)`
   *
   *  The passed styles will apply to these selectors.
   * - `[data-active=true]`
   *
   * @remarks
   *
   * Psuedo state order matters, `_active` should be declared last before other pseudo state props
   *
   * @example
   * Here's an example to with background.
   * ```
   * _active={{ bg: 'itemSelected' }}
   * ```
   */
  _active: '&:active:not(:disabled), &[data-active=true]:not(:disabled)',
  _focus: '&:focus:not(:disabled)',
  _focusWithin: '&:focus-within:not(:disabled)',
  /**
   * Styles for CSS Selector `&:focus-visible:not(:disabled)`
   *
   * To support browsers without `:focus-visible` we added `focus-visible` polyfill
   * which will add a data prop for elements that are focusable. The passed styles will
   * apply to these selectors.
   * - `[data-focus-visible-added]`
   *
   * @example
   * Here's an example to apply a focus ring:
   * ```
   * _focusVisible={{ boxShadow: 'focus' }}
   * ```
   */
  _focusVisible: '&[data-focus-visible-added]:not(:disabled), &:focus-visible:not(:disabled)',
  _visited: '&:visited:not(:disabled)',
  _even: '&:nth-of-type(even)',
  _odd: '&:nth-of-type(odd)',
  _first: '&:first-of-type',
  _last: '&:last-of-type',
  /**
   * Styles for CSS Selector `&:not(:first-of-type)`
   *
   * @example
   * Here's an example with margin top:
   * ```
   * _notFirst={{ marginTop: "space30" }}
   * ```
   */
  _notFirst: '&:not(:first-of-type)',
  _notLast: '&:not(:last-of-type)',
  _disabled: '&:disabled, &:disabled:focus, &:disabled:hover, &[aria-disabled=true], &[aria-disabled=true]:focus, &[aria-disabled=true]:hover',
  _checked: '&:checked, &[aria-checked=true]',
  _mixed: '&:indeterminate, &[aria-checked=mixed]',
  _selected: '&[aria-selected=true]',
  _invalid: '&:invalid, &[aria-invalid=true]',
  _pressed: '&[aria-pressed=true]',
  _readOnly: '&[aria-readonly=true], &[readonly]',
  _expanded: '&[aria-expanded=true]',
  _grabbed: '&[aria-grabbed=true]',
  /**
   *  @deprecated _groupHover is not recommended due to limited use-cases and potential for bugs.
   *
   * Please refer to the [BlockParty documentation](https://blockparty.endpoint.com/migration-guides/groupHover) for alternative approaches.
   */
  _groupHover: '[role=group]:hover &',
  _before: '&:before',
  _after: '&:after',
  _placeholder: '&::placeholder',
  __moz_focus_inner: '&::-moz-focus-inner',
  /**
   * Styles for CSS Selector '&::selection
   *
   * Only works with `color`, `background-color`, `text-decoration`, `text-shadow`
   *
   * @example
   * Here's an example where the highlighted text has special styles:
   * ```
   * _selection={{ backgroundColor: 'marigold100' }}
   * ```
   */
  _selection: '&::selection'
};

export { PseudoPropStyles };
